import { useState } from 'react';

import { cn, ia, iaRa, io, pluralize } from 'lib/helpers/utility';

import Badge from 'components/shared/Badge/Badge';
import Button from 'components/shared/Button/Button';
import DateRangeSelector from 'components/shared/DateRangeSelector/DateRangeSelector';

import {
  getActiveFilters,
  getFormulaOptions,
  signatureStatusOptions,
  textOptions
} from '../lib/helpers';

import TextInput from './TextInput';
import RangeSelector from './RangeSelector';
import InfiniteDropdown from './InfiniteDropdown';

const rangeTypes = ['range', 'number', 'number-range'];

const AppliedFilters = ({
  filters = {},
  defaultFilters = {},
  changeFilters,
  updateFilters,
  visibleCount,
  position = 'right'
}) => {
  const [count, setCount] = useState();

  const appliedFilters = getActiveFilters(filters).filter(([key]) => key !== 'search');

  if (!ia(appliedFilters)) return null;

  const visibleFilters = count ? appliedFilters.slice(0, count) : appliedFilters;

  const handleShowMore = () => {
    if (!visibleCount) return;
    setCount((prev) => (prev === undefined ? visibleCount : undefined));
  };

  return (
    <>
      {visibleFilters.map(([key, filter]) => {
        return iaRa(filter).map((_filter, index) => {
          const { title, icon, values, formula, type } = _filter;
          const pluralLabel = ia(values) && pluralize(title || '', values.length, ['uploaded by']);

          const [item] = (ia(values) && values) || [];

          const handleRemoveFilter = () => {
            changeFilters((prev) => {
              if (!ia(prev[key])) return prev;
              const filteredArray = prev[key].filter((f, filterIndex) => filterIndex !== index);
              if (!ia(filteredArray)) {
                const { [key]: _, ...rest } = prev;
                return rest;
              }
              return {
                ...prev,
                [key]: filteredArray
              };
            });
          };

          const handleFormulaChange = (formula) => {
            changeFilters((prev) => ({
              ...prev,
              [key]: prev[key].map((f, filterIndex) =>
                filterIndex === index ? { ...f, formula: formula.value } : f
              )
            }));
          };

          const handleOutsideClick = (items) => {
            if (!ia(items) && !io(items) && typeof items !== 'number') {
              handleRemoveFilter();
              return;
            }

            changeFilters((prev) => ({
              ...prev,
              [key]: prev[key].map((_filter, filterIndex) => {
                if (filterIndex !== index) return _filter;
                const activeFormula = formula || filters[key]?.formula;
                return {
                  ..._filter,
                  values: items,
                  formula: activeFormula || 'is',
                  ...(ia(items) &&
                    activeFormula !== 'is not' &&
                    !signatureStatusOptions.includes(activeFormula) && {
                      formula: items.length > 1 ? 'is any of' : 'is'
                    })
                };
              })
            }));
          };

          const buttonText = !ia(values, 1)
            ? item?.label
            : `${values.length} ${pluralLabel.toLowerCase()}`;

          return (
            <Button.Group key={key + '-' + index} variant="tertiary" size="xs">
              <Button
                icon={icon}
                text={title}
                iconColor="neutral"
                className="pointer-events-none whitespace-nowrap hover:bg-transparent active:bg-transparent"
              />
              <Button.Dropdown
                items={getFormulaOptions(type, iaRa(values)?.length, values)}
                dropdownClassName="rounded-[4px]"
                onClick={handleFormulaChange}
                active={formula}
                text={formula}
                className="whitespace-nowrap border-x-0"
                index={index}
              />
              {type === 'date-range' ? (
                <DateRangeSelector
                  value={values}
                  onChange={handleOutsideClick}
                  buttonClassName={cn(
                    'whitespace-nowrap rounded-none',
                    _filter.default && 'rounded-r-md'
                  )}
                  index={index}
                  position={position}
                  direction={defaultFilters[key]?.direction}
                />
              ) : rangeTypes.includes(type) ? (
                <RangeSelector
                  values={values}
                  onApply={handleOutsideClick}
                  buttonClassName={cn(
                    'whitespace-nowrap rounded-none',
                    _filter.default && 'rounded-r-md'
                  )}
                  initialOpenState={!values}
                  formula={formula}
                  numeric={_filter.numeric}
                  index={index}
                  position={position}
                />
              ) : type === 'text' && textOptions.includes(formula) ? (
                <TextInput
                  values={values}
                  onApply={handleOutsideClick}
                  formula={formula}
                  initialOpenState={!values}
                  index={index}
                  position={position}
                />
              ) : (
                <InfiniteDropdown
                  items={values}
                  data={defaultFilters[key]}
                  dropdownClassName={cn(
                    'absolute m-0 z-[100] shadow-xs',
                    position === 'left' ? 'right-0' : 'left-0'
                  )}
                  onChange={handleOutsideClick}
                  active={iaRa(values).includes(item?.value)}
                  text={buttonText}
                  withButton
                  index={index}
                  onWrongFilter={updateFilters}
                />
              )}
              {!_filter.default && (
                <Button
                  icon="close-linear"
                  iconColor="neutral"
                  className="border-l-0"
                  iconShade={700}
                  iconSize={16}
                  onClick={handleRemoveFilter}
                />
              )}
            </Button.Group>
          );
        });
      })}
      {visibleCount && visibleCount < appliedFilters.length && (
        <Badge
          color="neutral"
          size="medium"
          text={
            count < appliedFilters.length ? `+${appliedFilters.length - count} more` : 'Show less'
          }
          onClick={handleShowMore}
          isRounded={false}
        />
      )}
    </>
  );
};

export default AppliedFilters;
